import React from 'react';
import { Snackbar, Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import type { SnackbarProps } from './types';

const STYLES: Record<string, any> = {
  success: { bg: '#16a34a', icon: 'success' },
  error: { bg: '#dc2626', icon: 'error' },
  warning: { bg: '#d97706', icon: 'warning' },
  info: { bg: '#0891b2', icon: 'info' },
};

const ICONS: Record<string, React.ReactNode> = {
  success: <CheckCircleOutlineIcon sx={{ fontSize: 22 }} />,
  error: <ErrorOutlineIcon sx={{ fontSize: 22 }} />,
  warning: <WarningAmberIcon sx={{ fontSize: 22 }} />,
  info: <InfoOutlinedIcon sx={{ fontSize: 22 }} />,
};

const DURATION: Record<string, number> = {
  success: 4000,
  error: 9000,
  warning: 7000,
  info: 4000,
};

const CustomSnackbar: React.FC<SnackbarProps> = ({ open, onClose, message, severity = 'info', autoHideDuration, ...rest }) => {
  const style = STYLES[severity] || STYLES.info;
  const duration = autoHideDuration ?? DURATION[severity] ?? 4000;

  return (
    <Snackbar
      open={open}
      autoHideDuration={duration}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ mt: 7, maxWidth: 'calc(100vw - 32px)' }}
      {...rest}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          bgcolor: style.bg,
          color: '#fff',
          px: 2,
          py: 1.25,
          borderRadius: '10px',
          minWidth: '320px',
          maxWidth: '500px',
          width: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box',
          boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
        }}
      >
        <Box sx={{ flexShrink: 0, pt: 0.25 }}>{ICONS[style.icon]}</Box>
        <Typography
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {message}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ flexShrink: 0, color: '#fff', opacity: 0.8, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Snackbar>
  );
};

export default CustomSnackbar;
